// Paper dimensions in mm (landscape orientation, ISO 216)
const PAPER_MM = {
    A4: { width: 297, height: 210 },
    A3: { width: 420, height: 297 },
    A2: { width: 594, height: 420 }
};

// Page dimensions in points at 72 DPI (landscape) — matches PrintSpecMapSource
const PAGE_PTS = {
    A4: { width: 842, height: 595 },
    A3: { width: 1191, height: 842 },
    A2: { width: 1684, height: 1191 }
};

// Borders deducted from page for the printable site map area (from PrintSpecMapSource)
// Height = pageSizeBorder.height (195) + per-owner extra (50) = 245
const PAGE_BORDER_PTS = { width: 44, height: 245 };

const PT_TO_MM = 25.4 / 72;

// Maximum preview width (CSS pixels) — A2 landscape fills this
const MAX_PREVIEW_WIDTH = 560;
const PREVIEW_SCALE = MAX_PREVIEW_WIDTH / PAPER_MM.A2.width;

/**
 * Fixed container dimensions so the layout doesn't shift when switching paper sizes.
 * Based on the largest paper (A2 landscape).
 */
export const CONTAINER_DIMENSIONS = {
    width: MAX_PREVIEW_WIDTH,
    height: Math.round(PAPER_MM.A2.height * PREVIEW_SCALE)
};

/**
 * Parse an orientation string like "A4 Landscape" into its parts.
 */
export const parseOrientation = (orientation) => {
    const parts = orientation ? orientation.split(' ') : [];
    return {
        paperSize: parts[0] || 'A4',
        paperOrientation: parts[1] || 'Landscape'
    };
};

/**
 * Get paper preview dimensions in CSS pixels, proportionally correct
 * relative to actual paper sizes. All landscape since portrait is not supported.
 */
export const getPaperPreviewDimensions = (paperSize) => {
    const paper = PAPER_MM[paperSize] || PAPER_MM.A4;
    return {
        width: Math.round(paper.width * PREVIEW_SCALE),
        height: Math.round(paper.height * PREVIEW_SCALE)
    };
};

/** Get the actual printable map area on the PDF in mm (page size minus header/footer/sidebar borders). */
export const getMapAreaMm = (paperSize) => {
    const page = PAGE_PTS[paperSize] || PAGE_PTS.A4;
    return {
        width: (page.width - PAGE_BORDER_PTS.width) * PT_TO_MM,
        height: (page.height - PAGE_BORDER_PTS.height) * PT_TO_MM
    };
};

/** Get the map area as CSS pixels within the paper preview, positioned proportionally to the actual PDF page. */
export const getMapPreviewDimensions = (paperSize) => {
    const paper = PAPER_MM[paperSize] || PAPER_MM.A4;
    const paperPreview = getPaperPreviewDimensions(paperSize);
    const mapArea = getMapAreaMm(paperSize);
    return {
        width: Math.round(paperPreview.width * (mapArea.width / paper.width)),
        height: Math.round(paperPreview.height * (mapArea.height / paper.height))
    };
};

/** Calculate the effective scale denominator for the preview (increased so the same geographic extent is visible at fewer pixels). */
export const getEffectiveScale = (scale, paperSize) => {
    const mapArea = getMapAreaMm(paperSize);
    const mapPreview = getMapPreviewDimensions(paperSize);
    // Convert real map width from mm to screen pixels at 96 DPI, then ratio with preview pixels
    const realMapWidthScreenPx = mapArea.width / 25.4 * 96;
    return String(Math.round(parseInt(scale) * realMapWidthScreenPx / mapPreview.width));
};

/** Check if the AOI extends beyond the printable map area, using real map dimensions and the MapFish ground coverage formula. */
export const checkAoiCutoff = (aoi, scale, paperSize) => {
    const aoiExtent = aoi.getGeometry().getExtent();
    const aoiWidth = aoiExtent[2] - aoiExtent[0];
    const aoiHeight = aoiExtent[3] - aoiExtent[1];

    const scaleNum = parseInt(scale);
    const mapArea = getMapAreaMm(paperSize);

    // Ground coverage = map area in meters × scale denominator
    // mapArea is in mm, so /1000 converts to meters
    const visibleGroundWidth = (mapArea.width / 1000) * scaleNum;
    const visibleGroundHeight = (mapArea.height / 1000) * scaleNum;

    return aoiWidth > visibleGroundWidth || aoiHeight > visibleGroundHeight;
};
